/**
 * Mini-game score persistence utilities
 * Stores high scores and stats for ScoutIQ loading mini-games
 */

const STORAGE_KEY = 'dh.scoutiq.minigame_stats';
const STORAGE_VERSION = 1;

/**
 * Get default stats structure
 */
function getDefaultStats() {
  return {
    version: STORAGE_VERSION,
    highScores: {
      flashcards: 0,
      quizzes: 0,
      podcasts: 0,
      audiobooks: 0,
    },
    totalPlays: 0,
    lastPlayed: null,
  };
}

/**
 * Load all game stats from localStorage
 * @returns {Object} Stats object with highScores, totalPlays, lastPlayed
 */
export function getGameStats() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return getDefaultStats();
    }
    
    const stats = JSON.parse(raw);
    
    // Validate version and structure
    if (stats.version !== STORAGE_VERSION || !stats.highScores) {
      console.warn('[MiniGame] Invalid stats version, resetting to defaults');
      return getDefaultStats();
    }
    
    return stats;
  } catch (error) {
    console.warn('[MiniGame] Failed to load stats:', error);
    return getDefaultStats();
  }
}

/**
 * Get high score for a specific content type
 * @param {string} contentType - One of: flashcards, quizzes, podcasts, audiobooks
 * @returns {number} High score for that content type
 */
export function getHighScore(contentType) {
  const stats = getGameStats();
  return stats.highScores[contentType] || 0;
}

/**
 * Save game score and update high score if needed
 * @param {string} contentType - One of: flashcards, quizzes, podcasts, audiobooks
 * @param {number} score - Final score from this game session
 * @returns {Object} { saved: boolean, isNewHighScore: boolean, previousBest: number }
 */
export function saveGameScore(contentType, score) {
  try {
    const stats = getGameStats();
    
    // Validate content type
    if (!stats.highScores.hasOwnProperty(contentType)) {
      console.warn(`[MiniGame] Invalid content type: ${contentType}`);
      return { saved: false, isNewHighScore: false, previousBest: 0 };
    }
    
    const previousBest = stats.highScores[contentType];
    const isNewHighScore = score > previousBest;
    
    // Update stats
    if (isNewHighScore) {
      stats.highScores[contentType] = score;
    }
    stats.totalPlays += 1;
    stats.lastPlayed = new Date().toISOString();
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
    
    return {
      saved: true,
      isNewHighScore,
      previousBest,
    };
  } catch (error) {
    console.warn('[MiniGame] Failed to save score:', error);
    return { saved: false, isNewHighScore: false, previousBest: 0 };
  }
}

/**
 * Reset all stats (for testing or user preference)
 * @returns {boolean} Success status
 */
export function resetGameStats() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(getDefaultStats()));
    return true;
  } catch (error) {
    console.warn('[MiniGame] Failed to reset stats:', error);
    return false;
  }
}
